import { Alert, Button, Card, Form, Input, message, Modal, Spin, Badge, Statistic, Row, Col, Switch, Progress, Tooltip, Space, Typography, Divider } from "antd";
import { useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IoLogoWhatsapp, IoQrCode, IoRefresh, IoStop, IoPowerSharp, IoStatsChart, IoWarning, IoCheckmarkCircle, IoTime, IoSettings } from "react-icons/io5";
import { BsWhatsapp } from "react-icons/bs";
import { MdAutorenew, MdError } from "react-icons/md";
import { POST, WHATSAPP_API } from "../../utils/apiRequest";
import { API } from "../../config/Api";
import PageHeaders from "../../components/pageHeaders";
import { useForm } from "antd/es/form/Form";

// QR Code component for rendering QR codes from text
const QRCodeDisplay = ({ value, size = 256 }: { value: string; size?: number }) => {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>("");

  useEffect(() => {
    if (value) {
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(value)}`;
      setQrCodeDataUrl(qrUrl);
    }
  }, [value, size]);

  if (!value) return null;

  return (
    <div className="text-center">
      {qrCodeDataUrl && (
        <img
          src={qrCodeDataUrl}
          alt="QR Code"
          style={{ maxWidth: '100%', height: 'auto' }}
          onError={() => {
            console.error("QR Code generation failed");
            setQrCodeDataUrl("");
          }}
        />
      )}
    </div>
  );
};

interface WhatsAppSession {
  sessionId: string;
  companyId: string;
  companyName: string;
  connected: boolean;
  authenticated: boolean;
  phone_number?: string;
  status: string;
  created_at: string;
  last_active?: string;
}

interface MessageStats {
  total_messages: string;
  text_messages: string;
  pdf_messages: string;
  image_messages: string;
  first_message_date?: string;
  last_message_date?: string;
}

const { Text } = Typography;

const WhatsAppScreen = () => {
  const [form] = useForm();
  const navigate = useNavigate();
  const user = useSelector((state: any) => state?.User?.user);
  const office = user?.office;

  const [loading, setLoading] = useState(false);
  const [sessionStatus, setSessionStatus] = useState<WhatsAppSession | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [messageStats, setMessageStats] = useState<MessageStats | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [currentOperation, setCurrentOperation] = useState<string>('');

  const statusIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
    };
  }, []);

  const sessionId = office?.whatsapp_server_url;

  const getSessionStatus = useCallback(async () => {
    try {
      if (!autoRefresh) setLoading(true);
      const whatsappServerUrl = API.WHATSAPP_SERVER_URL;
      const response = await fetch(`${whatsappServerUrl}/status?sessionId=${sessionId}`);
      const data = await response.json();

      if (response.ok) {
        setSessionStatus(data);
        setLastUpdated(new Date());

        if (data.status === 'waiting_for_qr_scan' && !qrCode && !showQrModal) {
          setTimeout(() => getQrCode(false), 1000);
        }
      }
    } catch (error) {
      console.error("Error fetching session status:", error);
    } finally {
      if (!autoRefresh) setLoading(false);
    }
  }, [sessionId, autoRefresh, qrCode, showQrModal]);

  const getMessageStats = useCallback(async () => {
    try {
      const whatsappServerUrl = API.WHATSAPP_SERVER_URL;
      const response = await fetch(`${whatsappServerUrl}/reports/session/${sessionId}`);
      const data = await response.json();

      if (response.ok) {
        setMessageStats(data.stats);
      }
    } catch (error) {
      console.error("Error fetching message stats:", error);
    }
  }, [sessionId]);

  useEffect(() => {
    if (autoRefresh) {
      getSessionStatus();
      getMessageStats();

      statusIntervalRef.current = setInterval(() => {
        getSessionStatus();
        getMessageStats();
      }, 10000);
    }

    return () => {
      if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
    };
  }, [autoRefresh, getSessionStatus, getMessageStats]);

  useEffect(() => {
    getSessionStatus();
    getMessageStats();
  }, []);

  const resetSession = async () => {
    try {
      setLoading(true);
      setCurrentOperation('Resetting session...');

      const whatsappServerUrl = API.WHATSAPP_SERVER_URL;
      const response = await fetch(`${whatsappServerUrl}/reset-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
      });

      const data = await response.json();

      if (response.ok) {
        message.success("Session reset successfully!");
        setTimeout(() => {
          getSessionStatus();
          setCurrentOperation('');
        }, 1000);
      } else {
        message.error(data.message || "Failed to reset session");
        setCurrentOperation('');
      }
    } catch (error) {
      message.error("Error resetting session");
      setCurrentOperation('');
    } finally {
      setLoading(false);
    }
  };

  const initializeSession = async () => {
    try {
      setLoading(true);
      setCurrentOperation('Initializing session...');

      const whatsappServerUrl = API.WHATSAPP_SERVER_URL;

      const response = await fetch(`${whatsappServerUrl}/init-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          companyId: office?.id.toString(),
          companyName: office?.name,
          metadata: {
            officeId: office?.id,
            officeName: office?.name,
          }
        }),
      });

      const data = await response.json();

      if (response.ok) {
        message.success("WhatsApp session initialized successfully!");

        await POST(API.OFFICE_WHATSAPP_UPDATE, {
          office_id: office?.id,
          whatsapp_server_url: sessionId,
          whatsapp_session_id: sessionId,
        });

        setTimeout(() => {
          getSessionStatus();
          getQrCode(true);
          setCurrentOperation('');
        }, 1000);
      } else {
        message.error(data.message || "Failed to initialize session");
        setCurrentOperation('');
      }
    } catch (error) {
      message.error("Error initializing WhatsApp session");
      setCurrentOperation('');
    } finally {
      setLoading(false);
    }
  };

  const getQrCode = async (showModal = true) => {
    try {
      const whatsappServerUrl = API.WHATSAPP_SERVER_URL;
      const response = await fetch(`${whatsappServerUrl}/qr?sessionId=${sessionId}`);
      const data = await response.json();

      if (response.ok) {
        if (data.qr) {
          setQrCode(data.qr);
          if (showModal) setShowQrModal(true);
        } else if (data.qrCodes && data.qrCodes[sessionId]) {
          setQrCode(data.qrCodes[sessionId]);
          if (showModal) setShowQrModal(true);
        } else {
          if (showModal) message.warning("QR code not available. Session might already be authenticated.");
        }
      } else {
        if (showModal) message.error(data.message || "Failed to fetch QR code");
      }
    } catch (error) {
      if (showModal) message.error("Failed to fetch QR code");
    }
  };

  const disconnectSession = async () => {
    try {
      setLoading(true);
      setCurrentOperation('Disconnecting...');

      const whatsappServerUrl = API.WHATSAPP_SERVER_URL;
      const response = await fetch(`${whatsappServerUrl}/disconnect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
      });

      if (response.ok) {
        message.success("Session disconnected successfully!");
        setSessionStatus(null);
        setQrCode(null);
        setCurrentOperation('');
      } else {
        message.error("Failed to disconnect session");
        setCurrentOperation('');
      }
    } catch (error) {
      message.error("Error disconnecting session");
      setCurrentOperation('');
    } finally {
      setLoading(false);
    }
  };

  const reconnectSession = async (force = false) => {
    try {
      setLoading(true);
      setCurrentOperation('Reconnecting...');

      const whatsappServerUrl = API.WHATSAPP_SERVER_URL;
      const response = await fetch(`${whatsappServerUrl}/reconnect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, force }),
      });

      const data = await response.json();

      if (response.ok) {
        message.success("Reconnection initiated!");
        setTimeout(() => {
          getSessionStatus();
          getQrCode(false);
          setCurrentOperation('');
        }, 2000);
      } else {
        if (data.suggestion && data.suggestion.includes('force=true')) {
          Modal.confirm({
            title: 'Session Recovery Required',
            content: `${data.message}\n\nWould you like to force reconnect?`,
            onOk: () => reconnectSession(true),
            okText: 'Force Reconnect',
            cancelText: 'Cancel',
            okButtonProps: { danger: true }
          });
        } else {
          message.error(data.message || "Failed to reconnect session");
        }
        setCurrentOperation('');
      }
    } catch (error) {
      message.error("Error reconnecting session");
      setCurrentOperation('');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: any = {
      'ready': 'success',
      'authenticated': 'processing',
      'waiting_for_qr_scan': 'warning',
      'initializing': 'processing',
      'reconnect_failed': 'error',
      'recovery_failed': 'error',
      'disconnected': 'error',
      'auth_failed': 'error',
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status: string) => {
    const icons: any = {
      'ready': <IoCheckmarkCircle style={{ color: '#52c41a' }} />,
      'authenticated': <IoCheckmarkCircle style={{ color: '#1890ff' }} />,
      'waiting_for_qr_scan': <IoQrCode style={{ color: '#faad14' }} />,
      'initializing': <IoTime style={{ color: '#1890ff' }} />,
      'reconnect_failed': <MdError style={{ color: '#ff4d4f' }} />,
      'recovery_failed': <MdError style={{ color: '#ff4d4f' }} />,
      'auth_failed': <MdError style={{ color: '#ff4d4f' }} />,
      'disconnected': <IoWarning style={{ color: '#ff4d4f' }} />,
    };
    return icons[status] || <IoWarning style={{ color: '#d9d9d9' }} />;
  };

  const getStatusText = (status: string) => {
    const texts: any = {
      'ready': 'Connected & Ready',
      'authenticated': 'Authenticated',
      'waiting_for_qr_scan': 'Waiting for QR Scan',
      'disconnected': 'Disconnected',
      'auth_failed': 'Authentication Failed',
      'initializing': 'Initializing...',
      'reconnect_failed': 'Reconnection Failed',
      'recovery_failed': 'Recovery Failed',
    };
    return texts[status] || 'Unknown Status';
  };

  return (
    <div>
      <PageHeaders title="WhatsApp Integration" breadcrumb="WhatsApp" />

      <div className="screenBox">
        <Row gutter={[16, 16]}>
          {/* Connection Status & Controls */}
          <Col xs={24} lg={12}>
            <Card
              title={
                <Space>
                  <BsWhatsapp className="text-success" />
                  <span>Connection Status</span>
                  {lastUpdated && (
                    <Text type="secondary" style={{ fontSize: '11px', fontWeight: 'normal' }}>
                      Updated: {lastUpdated.toLocaleTimeString()}
                    </Text>
                  )}
                </Space>
              }
              extra={
                <Space size="small">
                  <Tooltip title="Auto refresh">
                    <Switch
                      checked={autoRefresh}
                      onChange={setAutoRefresh}
                      size="small"
                    />
                  </Tooltip>
                  <Button
                    icon={<IoRefresh />}
                    onClick={() => getSessionStatus()}
                    loading={loading && !autoRefresh}
                    size="small"
                  />
                </Space>
              }
              size="small"
            >
              {currentOperation && (
                <div className="mb-2">
                  <Text type="secondary" style={{ fontSize: '12px' }}>{currentOperation}</Text>
                  <Progress percent={100} size="small" status="active" showInfo={false} />
                </div>
              )}

              {loading && !sessionStatus ? (
                <div className="text-center py-4">
                  <Spin />
                </div>
              ) : sessionStatus ? (
                <>
                  <Space className="mb-3">
                    {getStatusIcon(sessionStatus.status)}
                    <Badge
                      status={getStatusColor(sessionStatus.status)}
                      text={getStatusText(sessionStatus.status)}
                    />
                  </Space>

                  <Row gutter={[8, 8]}>
                    {sessionStatus.phone_number && (
                      <Col span={24}>
                        <Text strong>Phone:</Text> <Text>{sessionStatus.phone_number}</Text>
                      </Col>
                    )}
                    <Col span={24}>
                      <Text strong>Session:</Text> <Text copyable>{sessionStatus.sessionId}</Text>
                    </Col>
                  </Row>

                  <Divider style={{ margin: '12px 0' }} />

                  <Space wrap>
                    {sessionStatus.status === 'ready' ? (
                      <Button
                        danger
                        icon={<IoStop />}
                        onClick={disconnectSession}
                        loading={loading}
                        size="small"
                      >
                        Disconnect
                      </Button>
                    ) : (
                      <>
                        <Button
                          type="primary"
                          icon={<IoPowerSharp />}
                          onClick={() => reconnectSession(false)}
                          loading={loading}
                          size="small"
                        >
                          Reconnect
                        </Button>

                        {['reconnect_failed', 'recovery_failed', 'initializing'].includes(sessionStatus.status) && (
                          <Button
                            icon={<IoSettings />}
                            onClick={resetSession}
                            loading={loading}
                            danger
                            size="small"
                          >
                            Reset
                          </Button>
                        )}

                        <Button
                          icon={<IoQrCode />}
                          onClick={() => getQrCode(true)}
                          type={sessionStatus.status === 'waiting_for_qr_scan' ? 'primary' : 'default'}
                          size="small"
                        >
                          QR Code
                        </Button>
                      </>
                    )}
                  </Space>
                </>
              ) : (
                <>
                  <Alert
                    message="No active session"
                    description="Initialize WhatsApp to start"
                    type="info"
                    showIcon
                    className="mb-3"
                  />

                  <Button
                    type="primary"
                    onClick={initializeSession}
                    loading={loading}
                    icon={<IoLogoWhatsapp />}
                    block
                  >
                    Initialize WhatsApp Session
                  </Button>
                </>
              )}
            </Card>
          </Col>

          {/* Message Statistics */}
          <Col xs={24} lg={12}>
            <Card
              title="Message Statistics"
              extra={
                <Space size="small">
                  <Button
                    icon={<IoStatsChart />}
                    onClick={() => navigate("/Auth/whatsapp/statistics")}
                    size="small"
                    type="link"
                  >
                    Details
                  </Button>
                </Space>
              }
              size="small"
            >
              {messageStats ? (
                <Row gutter={[16, 16]}>
                  <Col xs={12} sm={12}>
                    <Statistic
                      title="Total"
                      value={messageStats.total_messages}
                      prefix={<IoLogoWhatsapp style={{ color: '#25D366' }} />}
                      valueStyle={{ fontSize: '20px' }}
                    />
                  </Col>
                  <Col xs={12} sm={12}>
                    <Statistic
                      title="Text"
                      value={messageStats.text_messages}
                      valueStyle={{ fontSize: '20px' }}
                    />
                  </Col>
                  <Col xs={12} sm={12}>
                    <Statistic
                      title="PDF"
                      value={messageStats.pdf_messages}
                      valueStyle={{ fontSize: '20px' }}
                    />
                  </Col>
                  <Col xs={12} sm={12}>
                    <Statistic
                      title="Image"
                      value={messageStats.image_messages}
                      valueStyle={{ fontSize: '20px' }}
                    />
                  </Col>
                </Row>
              ) : (
                <Alert
                  message="No statistics available"
                  type="info"
                  showIcon
                />
              )}
            </Card>
          </Col>
        </Row>
      </div>

      {/* QR Code Modal */}
      <Modal
        title={<div> Scan QR Code<br/> <Text type="secondary" style={{ fontSize: '11px', fontWeight: 'normal' }}>Open WhatsApp → Settings → Linked Devices → Link a Device</Text></div>}
        open={showQrModal}
        onCancel={() => setShowQrModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowQrModal(false)}>Close</Button>,
          <Button key="refresh" type="primary" onClick={() => getQrCode(true)} icon={<IoRefresh />}>
            Refresh
          </Button>
        ]}
        width={400}
        centered
      >
        {qrCode ? (
          <div className="text-center py-4">
            {qrCode.startsWith('data:image') || qrCode.startsWith('http') ? (
              <img src={qrCode} alt="QR Code" style={{ maxWidth: '100%' }} />
            ) : (
              <QRCodeDisplay value={qrCode} size={300} />
            )}
            <div className="mt-3" style={{ padding: '16px', background: '#f5f5f5', borderRadius: '8px' }}>
              <Text strong style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>
                How to Scan
              </Text>
              <Text type="secondary" style={{ fontSize: '13px', lineHeight: '1.6' }}>
                Open WhatsApp → Settings → Linked Devices → Link a Device
              </Text>
            </div>
          </div>
        ) : (
          <div className="text-center py-5">
            <Spin size="large" />
            <div className="mt-2">Loading QR Code...</div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default WhatsAppScreen;
